import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Badge, Modal, LoadingSkeleton, FilterBar, FilterSelect, FilterSearch } from '../ui';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { hasAnyRole } from '../../utils/roles';
import { safeErrorMessage } from '../../utils/apiError';
import useApi from '../portal/useApi';
import { formatDate, termLabel } from '../portal/format';
import StudentField from './StudentField';
import portal from '../portal/Portal.module.css';
import styles from './Houses.module.css';

// Mirrors backend/houses.py's RECORD_ROLES and models.py's BOARD_CATEGORIES
const RECORD_ROLES = ['administrator', 'principal', 'head_of_academics'];
const CATEGORY_LABELS = {
  discipline: 'Good discipline',
  academic: 'Academic',
  sports: 'Sports',
  leadership: 'Leadership',
  service: 'Service',
  misconduct: 'Misconduct',
  fighting: 'Fighting',
  other: 'Other',
};
const KIND = {
  commendation: { label: 'Commendation', badge: 'success' },
  disciplinary: { label: 'Disciplinary', badge: 'danger' },
};
const NETWORK_ERROR = 'Network error. Please check your connection and try again.';
const today = () => new Date().toISOString().slice(0, 10);
const EMPTY_ENTRY = { student_id: '', kind: 'commendation', category: 'discipline', title: '', description: '', date: '' };

function EntryModal({ token, entry, students, onClose, onSaved }) {
  const isEdit = Boolean(entry?.id);
  const [form, setForm] = useState(EMPTY_ENTRY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!entry) return;
    setError('');
    setForm(isEdit
      ? { student_id: String(entry.student_id), kind: entry.kind, category: entry.category, title: entry.title, description: entry.description || '', date: entry.date }
      : { ...EMPTY_ENTRY, date: today() });
  }, [entry, isEdit]);

  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const body = { kind: form.kind, category: form.category, title: form.title, description: form.description, date: form.date };
      const response = await fetch(`/api/v1/houses/board${isEdit ? `/${entry.id}` : ''}`, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(isEdit ? body : { ...body, student_id: Number(form.student_id) }),
      });
      if (response.ok) onSaved();
      else setError(await safeErrorMessage(response, 'Failed to save entry'));
    } catch {
      setError(NETWORK_ERROR);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={Boolean(entry)} onClose={onClose} title={isEdit ? 'Edit entry' : 'Add to the Special Board'} width="540px">
      <form onSubmit={handleSubmit}>
        {error && <div className="alert alert-error">{error}</div>}
        {isEdit ? (
          <p className={styles.hint}>{entry.student_name}{entry.class_name ? ` · ${entry.class_name}` : ''}</p>
        ) : (
          <StudentField id="entry-student" students={students} value={form.student_id} onChange={(value) => setForm((prev) => ({ ...prev, student_id: value }))} />
        )}

        <div className={styles.choiceGroup}>
          <span className={styles.choiceLabel}>Type</span>
          <div className={styles.choiceRow}>
            {Object.entries(KIND).map(([value, item]) => (
              <label key={value} className={styles.choice}>
                <input type="radio" name="entry-kind" checked={form.kind === value} onChange={() => setForm((prev) => ({ ...prev, kind: value }))} />
                {item.label}
              </label>
            ))}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="entry-category">Category</label>
            <select id="entry-category" value={form.category} onChange={set('category')}>
              {Object.entries(CATEGORY_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="entry-date">Date</label>
            <input id="entry-date" type="date" value={form.date} onChange={set('date')} required />
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="entry-title">What happened</label>
          <input id="entry-title" type="text" value={form.title} onChange={set('title')} required maxLength={200} placeholder="e.g. Scored the winning goal against St. Mary's" />
        </div>
        <div className="form-group">
          <label htmlFor="entry-description">Details (optional)</label>
          <textarea id="entry-description" rows={3} value={form.description} onChange={set('description')} />
        </div>
        <p className={styles.hint}>
          {form.kind === 'disciplinary'
            ? 'Disciplinary entries are visible to staff only. Parents and students never see them.'
            : "Commendations are visible to the student's parents. Board entries do not change house scores."}
        </p>
        <div className={styles.modalActions}>
          <Button variant="primary" type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save entry'}</Button>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
        </div>
      </form>
    </Modal>
  );
}

// The Special Board: commendable and disciplinary notes about students.
// Separate from house scores — nothing here carries points.
function SpecialBoard({ token, user }) {
  const canRecord = hasAnyRole(user, RECORD_ROLES);
  const [kind, setKind] = useState('');
  const [category, setCategory] = useState('');
  const [classId, setClassId] = useState('');
  const [termId, setTermId] = useState('');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState('');

  const params = new URLSearchParams();
  if (kind) params.set('kind', kind);
  if (category) params.set('category', category);
  if (classId) params.set('class_id', classId);
  if (termId) params.set('term_id', termId);
  const board = useApi(token, `/api/v1/houses/board?${params}`);
  const terms = useApi(token, '/api/v1/terms');
  const classes = useApi(token, '/api/v1/classes');
  const students = useApi(token, canRecord ? '/api/v1/students' : null);

  const term = search.trim().toLowerCase();
  const all = board.data || [];
  const rows = all.filter((row) => !term || [row.student_name, row.title, row.description].some((value) => value?.toLowerCase().includes(term)));
  const filtersActive = Boolean(kind || category || classId || termId || term);
  const clearFilters = () => { setKind(''); setCategory(''); setClassId(''); setTermId(''); setSearch(''); };

  const handleDelete = async () => {
    setError('');
    try {
      const response = await fetch(`/api/v1/houses/board/${deleting.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (response.ok) board.reload();
      else setError(await safeErrorMessage(response, 'Failed to delete entry'));
    } catch {
      setError(NETWORK_ERROR);
    } finally {
      setDeleting(null);
    }
  };

  const columns = [
    { header: 'Date', render: (row) => formatDate(row.date) },
    { header: 'Student', render: (row) => (<div><strong>{row.student_name}</strong><div className={styles.entryDescription}>{[row.class_name, row.house_name].filter(Boolean).join(' · ') || '—'}</div></div>) },
    { header: 'Type', render: (row) => <Badge variant={KIND[row.kind]?.badge || 'default'}>{KIND[row.kind]?.label || row.kind}</Badge> },
    { header: 'Category', render: (row) => CATEGORY_LABELS[row.category] || row.category },
    { header: 'Entry', render: (row) => (<div><div className={styles.entryTitle}>{row.title}</div>{row.description && <div className={styles.entryDescription}>{row.description}</div>}</div>) },
    { header: 'Recorded by', render: (row) => row.recorded_by_name || '—' },
  ];
  if (canRecord) {
    columns.push({
      header: '',
      render: (row) => (
        <div className={styles.rowActions}>
          <Button variant="ghost" size="sm" onClick={() => setEditing(row)} aria-label={`Edit entry for ${row.student_name}`}><PencilIcon style={{ width: 14, height: 14 }} /></Button>
          <Button variant="ghost" size="sm" onClick={() => setDeleting(row)} aria-label={`Delete entry for ${row.student_name}`}><TrashIcon style={{ width: 14, height: 14, color: 'var(--color-danger)' }} /></Button>
        </div>
      ),
    });
  }

  return (
    <div>
      <div className={portal.header}>
        <div>
          <h2 className={portal.title}>Special Board</h2>
          <p className={portal.subtitle}>
            Commendations and disciplinary notes about students. {canRecord ? 'These are a record only and do not change house scores.' : 'You can see entries for your own classes.'}
          </p>
        </div>
        {canRecord && (
          <Button variant="primary" size="sm" onClick={() => setEditing({})}>
            <PlusIcon style={{ width: 16, height: 16 }} />
            Add entry
          </Button>
        )}
      </div>

      <FilterBar shown={rows.length} total={all.length} onClear={filtersActive ? clearFilters : undefined}>
        <FilterSearch value={search} onChange={setSearch} placeholder="Student or entry text" />
        <FilterSelect label="Type" value={kind} onChange={setKind} options={Object.entries(KIND).map(([value, item]) => ({ value, label: item.label }))} allLabel="All types" />
        <FilterSelect label="Category" value={category} onChange={setCategory} options={Object.entries(CATEGORY_LABELS).map(([value, label]) => ({ value, label }))} allLabel="All categories" />
        <FilterSelect label="Class" value={classId} onChange={setClassId} options={(classes.data || []).map((item) => ({ value: String(item.id), label: item.display_name }))} allLabel="All classes" />
        <FilterSelect label="Term" value={termId} onChange={setTermId} options={(terms.data || []).map((item) => ({ value: String(item.id), label: termLabel(item) }))} allLabel="All time" />
      </FilterBar>

      {(error || board.error) && <div className="alert alert-error">{error || board.error}</div>}

      <Card>
        {board.loading ? <LoadingSkeleton type="text" rows={6} /> : (
          <Table columns={columns} data={rows} emptyMessage={filtersActive ? 'No entries match these filters.' : 'Nothing on the board yet.'} />
        )}
      </Card>

      <EntryModal token={token} entry={editing} students={students.data || []} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); board.reload(); }} />

      <Modal isOpen={Boolean(deleting)} onClose={() => setDeleting(null)} title="Delete entry">
        {deleting && (
          <div>
            <p className={styles.hint}>Delete <strong>{deleting.title}</strong> about {deleting.student_name}? This cannot be undone.</p>
            <div className={styles.modalActions}>
              <Button variant="danger" onClick={handleDelete}>Delete entry</Button>
              <Button variant="secondary" onClick={() => setDeleting(null)}>Keep it</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default SpecialBoard;
